// 공구 사용·교체 시기(매거진 탭 2차 기능) - 순수 로직/포맷만 모아 둔다.
// EqpMagazineTab 과 그 하위 컴포넌트가 이 파일의 함수만 쓰고, 검증은 eqpToolLifeFormat.test.js 에서 한다.
import dayjs from 'dayjs';

// ============================= 상태(level) =============================
export const TOOL_LEVEL_LABEL = {
  OK: '여유',
  WARN: '교체 임박',
  OVER: '기준 초과',
  NONE: '기준 없음',
};

export const getToolLevelLabel = (level) => TOOL_LEVEL_LABEL[level] || TOOL_LEVEL_LABEL.NONE;

// 표/칩에 쓰는 MUI 표준 색 이름
export const getToolLevelChipColor = (level) => {
  switch (level) {
    case 'OK': return 'success';
    case 'WARN': return 'warning';
    case 'OVER': return 'error';
    default: return 'default';
  }
};

// 진행 막대·포트 그리드 등 theme 팔레트 색이 직접 필요한 곳(인라인 스타일)
export const getToolLevelColor = (theme, level) => {
  switch (level) {
    case 'OK': return theme.palette.success.main;
    case 'WARN': return theme.palette.warning.main;
    case 'OVER': return theme.palette.error.main;
    default: return theme.palette.grey[400];
  }
};

// ============================= 숫자/시간 포맷 =============================
// 진행 막대 폭(%) - 기준 초과(100% 넘음)여도 막대는 100에서 멈춘다. 음수/NaN은 0.
export const clampRatioPct = (ratio) => {
  const r = Number(ratio);
  if (!Number.isFinite(r) || r <= 0) return 0;
  return Math.min(100, r * 100);
};

// 표에 보이는 "N%" 글자 - 막대와 달리 100% 넘는 실제 값을 그대로 보여준다.
export const formatRatioPct = (ratio) => {
  if (ratio === null || ratio === undefined || ratio === '') return '-';
  const r = Number(ratio);
  if (!Number.isFinite(r)) return '-';
  return `${Math.round(r * 100)}%`;
};

// 숫자만(단위 없이) - 표의 숫자 칸처럼 열 제목에 이미 단위("가공(분)")가 있는 곳에서 쓴다.
export const formatMinNumber = (min) => {
  if (min === null || min === undefined || min === '') return '-';
  const m = Number(min);
  if (!Number.isFinite(m)) return '-';
  return Math.round(m).toLocaleString();
};

export const formatToolMin = (min) => {
  const n = formatMinNumber(min);
  return n === '-' ? '-' : `${n}분`;
};

// 상단 요약 카드의 "가동 시간" - 분을 시간(소수 1자리)으로 바꾼다.
export const formatHours = (min) => {
  if (min === null || min === undefined || min === '') return '-';
  const m = Number(min);
  if (!Number.isFinite(m)) return '-';
  return `${(m / 60).toFixed(1)}h`;
};

export const formatDateTime = (iso) => (iso ? dayjs(iso).format('YYYY-MM-DD HH:mm:ss') : null);
export const formatTime = (iso) => (iso ? dayjs(iso).format('HH:mm:ss') : null);

// "기준 대비" 칸의 "/ 기준" 글자 - limitMin 이 없으면(기준 미설정) 안내 문구
export const formatLimitText = (limitMin) => {
  const m = Number(limitMin);
  return Number.isFinite(m) && m > 0 ? `/ ${Math.round(m).toLocaleString()}분` : '기준 없음';
};

// ============================= 공구 목록/매핑 =============================
// toolNo → tool 레코드 - 매거진 포트 그리드에서 포트의 toolNo 로 level/ratio 를 찾을 때 쓴다.
export const buildToolByNo = (tools) => {
  const map = {};
  (tools || []).forEach((t) => { if (t && t.toolNo != null) map[t.toolNo] = t; });
  return map;
};

// 공구 표 정렬 - 현재 장착 공구를 맨 위로, 나머지는 공구번호 오름차순.
export const sortToolRows = (tools) => {
  const arr = Array.isArray(tools) ? [...tools] : [];
  return arr.sort((a, b) => {
    const curA = !!a?.current;
    const curB = !!b?.current;
    if (curA !== curB) return curA ? -1 : 1;
    return (Number(a?.toolNo) || 0) - (Number(b?.toolNo) || 0);
  });
};

// ============================= 타임라인 범위 =============================
export const TIMELINE_RANGES = [
  { key: 'today', label: '24시간', hours: 24 },
  { key: '3d', label: '3일', hours: 72 },
  { key: '7d', label: '7일', hours: 168 },
];

// 범위 키 → API 쿼리에 쓸 {from, to}. to는 now, from은 now - hours.
// 서버는 시간대 없는 로컬 시각(KST, 'YYYY-MM-DDTHH:mm:ss')만 받는다 — toISOString()의 UTC 'Z' 형식은 400으로 거절된다.
const LOCAL_DT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
export const rangeToFromTo = (rangeKey, now = new Date()) => {
  const opt = TIMELINE_RANGES.find((r) => r.key === rangeKey) || TIMELINE_RANGES[0];
  const to = dayjs(now);
  const from = to.subtract(opt.hours, 'hour');
  return { from: from.format(LOCAL_DT_FORMAT), to: to.format(LOCAL_DT_FORMAT) };
};

// ============================= SVG 타임라인 스케일링 =============================
const HOUR_MS = 3600e3;

// 시각(ms) → x 픽셀(0~width). 범위 밖은 양쪽 끝으로 clamp.
export const timeToX = (ms, t0, t1, width) => {
  if (!Number.isFinite(ms) || !Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return 0;
  const clamped = Math.min(t1, Math.max(t0, ms));
  return ((clamped - t0) / (t1 - t0)) * width;
};

// 보이는 구간 길이(ms)에 맞는 눈금 간격 - 눈금이 대략 12개를 넘지 않는 가장 촘촘한 간격을 고른다.
export const pickTickStepMs = (durationMs) => {
  const stepsHour = [1, 2, 3, 6, 12, 24, 48];
  for (let i = 0; i < stepsHour.length; i += 1) {
    const step = stepsHour[i] * HOUR_MS;
    if (durationMs / step <= 12) return step;
  }
  return stepsHour[stepsHour.length - 1] * HOUR_MS;
};

// t0~t1(ms) 구간의 눈금 목록 - [{ms, label}]. 1일(24h) 이상 간격이면 "MM/DD", 아니면 "HH:mm".
// 눈금은 그 날 0시를 기준으로 간격의 배수가 되는 깔끔한 시각에 찍는다(예: 6시간 간격이면 00/06/12/18시).
export const buildTimeTicks = (t0, t1) => {
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return [];
  const step = pickTickStepMs(t1 - t0);
  const dayMode = step >= 24 * HOUR_MS;
  let t = dayjs(t0).startOf('day').valueOf();
  while (t < t0) t += step;
  const ticks = [];
  for (; t <= t1; t += step) {
    const d = dayjs(t);
    ticks.push({ ms: t, label: dayMode ? d.format('MM/DD') : d.format('HH:mm') });
  }
  return ticks;
};

// ============================= 구간/시점 → 타임라인 막대 =============================
// 운전 상태(STATE) 구간 - EqpDayTimeline 과 같은 클리핑 방식(보이는 범위로 자르고 폭 0 이하는 버림)을
// 날짜 상대 분 단위가 아니라 ms 타임스탬프 도메인으로 그대로 쓴다(여러 날짜 범위를 다루므로).
export const buildStateBars = (intervals, t0, t1, width) => {
  if (!Array.isArray(intervals)) return [];
  return intervals
    .map((iv) => {
      const stMs = dayjs(iv.stDt).valueOf();
      const edMs = iv.edDt ? dayjs(iv.edDt).valueOf() : t1;
      const x = timeToX(stMs, t0, t1, width);
      const xEnd = timeToX(edMs, t0, t1, width);
      return { val: iv.val, stDt: iv.stDt, edDt: iv.edDt, x, w: Math.max(0, xEnd - x) };
    })
    .filter((b) => b.w > 0);
};

// 주축 공구(tools) 구간 - 막대 + T# 라벨
export const buildToolBars = (tools, t0, t1, width) => {
  if (!Array.isArray(tools)) return [];
  return tools
    .map((t) => {
      const stMs = dayjs(t.stDt).valueOf();
      const edMs = t.edDt ? dayjs(t.edDt).valueOf() : t1;
      const x = timeToX(stMs, t0, t1, width);
      const xEnd = timeToX(edMs, t0, t1, width);
      return { toolNo: t.toolNo, stDt: t.stDt, edDt: t.edDt, x, w: Math.max(0, xEnd - x) };
    })
    .filter((b) => b.w > 0);
};

// M06 교환 매크로(O9001)인지 - 그 외 O9xxx 는 교환 전후 보조 매크로(얇은 눈금으로 표시).
export const isM06Macro = (val) => val === 'O9001';

// 매크로 호출(macros) 시점 - 각 점의 x좌표 + M06 여부(강조 표시용)
export const buildMacroMarks = (macros, t0, t1, width) => {
  if (!Array.isArray(macros)) return [];
  return macros.map((m) => ({
    val: m.val,
    stDt: m.stDt,
    x: timeToX(dayjs(m.stDt).valueOf(), t0, t1, width),
    isM06: isM06Macro(m.val),
  }));
};
